import type { FooterSection } from '@/data/types';
import { resolveImagePath } from '@/utils/imageMap';

interface FooterProps {
  data: FooterSection;
}

export function Footer({ data }: FooterProps) {
  const { contactInfo, address, businessHours, facilityAccess, certifications, avionicsService, copyright } = data;

  return (
    <footer id="colophon" className="bg-gray-100 py-12">
      <div className="container mx-auto px-4">
        {/* Main footer content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
          {/* Left Column - Contact Info, Address, Hours, Facility Access */}
          <div className="space-y-8">
            {/* Contact Information */}
            <section>
              <h2 className="text-lg font-bold text-gray-800 mb-4">Contact Information</h2>
              <div className="text-gray-700 space-y-1">
                <p>Phone: {contactInfo.phone}</p>
                <p>Fax: {contactInfo.fax}</p>
                <p>
                  E-mail:{' '}
                  <a
                    href={`mailto:${contactInfo.email}`}
                    className="text-cyan-500 hover:text-cyan-600 transition-colors"
                  >
                    {contactInfo.email}
                  </a>
                </p>
              </div>
            </section>

            {/* Address */}
            <section>
              <h2 className="text-lg font-bold text-gray-800 mb-4">Address</h2>
              <div className="text-gray-700 space-y-1">
                <p>{address.company}</p>
                <p>{address.location}</p>
                <p>{address.street}</p>
                <p>{address.city}, {address.state} {address.zip}</p>
              </div>
            </section>

            {/* Business Hours */}
            <section>
              <h2 className="text-lg font-bold text-gray-800 mb-4">Business Hours</h2>
              <div className="text-gray-700 space-y-1">
                <p>{businessHours.weekdays}</p>
                <p>{businessHours.saturday}</p>
                <p>{businessHours.sunday}</p>
              </div>
            </section>

            {/* Facility Access */}
            <section>
              <h2 className="text-lg font-bold text-gray-800 mb-4">Facility Access</h2>
              <p className="text-gray-700 text-sm leading-relaxed">
                {facilityAccess.description}
              </p>
            </section>
          </div>

          {/* Center Column - Certifications */}
          <div>
            <section>
              <h2 className="text-lg font-bold text-gray-800 mb-4">Certifications</h2>
              <div className="text-gray-700 space-y-2">
                <p className="font-semibold">{certifications.title}</p>
                <p>{certifications.certificateNumber}</p>
                <p className="mb-3">
                  <a
                    href={certifications.downloadLink.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-cyan-500 hover:text-cyan-600 transition-colors"
                  >
                    {certifications.downloadLink.text}
                  </a>
                </p>
                <p className="text-sm leading-relaxed">
                  {certifications.description}
                </p>
              </div>
            </section>
          </div>

          {/* Right Column - Avionics Installation & Service */}
          <div>
            <section>
              <h2 className="text-lg font-bold text-gray-800 mb-4">{avionicsService.title}</h2>
              <div className="space-y-4">
                <div className="w-full">
                  <img
                    src={resolveImagePath(avionicsService.image)}
                    alt="Avionics Installation & Service"
                    className="w-full h-auto object-cover rounded"
                  />
                </div>
                <p className="text-gray-700 text-sm leading-relaxed">
                  {avionicsService.description}
                </p>
                <p>
                  <a
                    href={avionicsService.readMoreLink}
                    className="text-cyan-500 hover:text-cyan-600 transition-colors inline-flex items-center"
                  >
                    Read More
                    <svg
                      className="w-4 h-4 ml-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </a>
                </p>
              </div>
            </section>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-gray-300 pt-4">
          <div className="text-center text-sm text-gray-600">
            <p>
              Copyright © {copyright.year}{' '}
              <a
                href={copyright.companyLink}
                className="text-gray-800 hover:text-cyan-600 transition-colors"
              >
                {copyright.company}
              </a>
              . | Mudita | Developed By{' '}
              <a
                href={copyright.themeLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-800 hover:text-cyan-600 transition-colors"
              >
                {copyright.theme}
              </a>
              {' | '}Powered by:{' '}
              <a
                href={copyright.platformLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-800 hover:text-cyan-600 transition-colors"
              >
                {copyright.platform}
              </a>{' '}
              |
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}